import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { PapiUser } from '@wix/da-papi-types';
import { Url, URLS } from '@wix/da-url';
import { useTranslation } from 'react-i18next';
import {
  BiContextProvidedValue,
  BiData,
  AddToGroupSelectBiEvent,
  DefaultBiEvent,
} from '@wix/da-bi/pkg/events';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { ListItemButton } from '@wix/da-ds/pkg/List/ListItem';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import RecommendedIcon from '@wix/da-ds/pkg/Icons/Recommended';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import StreamedList from '../../../../StreamedList';
import BasicUserAvatar from '../../../../User/_partials/BasicUserAvatar';
import { useNormalizedUserStream } from '../../../../utils/hooks/useStream';
import { recommendedGroupsStreamId } from '../../../redux/constants';
import { initRecommendedGroups } from '../../../redux/actions';
import s from './RecommendedGroupList.scss';

export interface RecommendedGroupListProps {
  deviationId: number;
  filterValue?: string;
  onSelect: (group: PapiUser) => void;
}

const RecommendedGroupList: React.FC<RecommendedGroupListProps> = ({
  deviationId,
  filterValue,
  onSelect,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    items: recommendedGroups,
    isInitialized,
    isFetching,
  } = useNormalizedUserStream(recommendedGroupsStreamId, 500);

  useEffect(() => {
    if (isInitialized || isFetching) {
      return;
    }
    dispatch(initRecommendedGroups());
  }, [recommendedGroups.length, isInitialized, isFetching, dispatch]);

  if (isFetching && recommendedGroups.length === 0) {
    return <LoadingIndicator view="llama" className={s['loader']} />;
  }

  if (!isFetching && recommendedGroups.length === 0) {
    return (
      <div className={s['root']}>
        <Section className={s['empty']}>
          <Heading className={s['empty-header']}>
            {t('addToGroupPopup.empty.header')}
          </Heading>
          <div className={s['empty-text']}>
            {t('addToGroupPopup.empty.text')}
          </div>
          <GhostButton
            className={s['empty-cta-button']}
            variant="approval-green"
            size="medium"
            href={URLS.GROUPS}
            biData={BiData<DefaultBiEvent>({
              evid: 2,
              click_info: 'addToGroup.emptyState',
              sectionname: BiContextProvidedValue,
              widgetname: BiContextProvidedValue,
              typeid: BiContextProvidedValue,
              itemid: BiContextProvidedValue,
            })}
          >
            {t('addToGroupPopup.empty.CTA.button')}
          </GhostButton>
        </Section>
      </div>
    );
  }

  return (
    <div className={s['root']}>
      <div className={s['groups-description']}>
        {t('addToGroupPopup.recommended.subtitle')}
      </div>
      <div className={s['groups-header']}>
        <RecommendedIcon size={IconSize.SMALLER} />
        <Heading className={s['groups-title']}>
          {t('networkBar.subheader.recommendedGroups')}
        </Heading>
      </div>
      <StreamedList
        streamId={recommendedGroupsStreamId}
        className={s['list']}
        wrapperClassName={s['scrollable']}
      >
        {recommendedGroups.map(group =>
          !filterValue ||
          group.username.toLowerCase().includes(filterValue.toLowerCase()) ? (
            <ListItemButton
              key={group.userId}
              href={Url.userLink(group.username)}
              target="_blank"
              rel="noopener noreferrer"
              biData={BiData<AddToGroupSelectBiEvent>({
                evid: 341,
                deviationid: deviationId,
                groupid: group.useridUuid,
                source_type: BiContextProvidedValue,
              })}
              label={group.username}
              prefix={<BasicUserAvatar user={group} size={35} />}
              suffix={
                <GhostButton
                  variant="main"
                  size="medium"
                  href={Url.userLink(group.username)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={s['visit-group-button']}
                  biData={BiData<DefaultBiEvent>({
                    evid: 2,
                    click_info: 'addToGroup.visitRecommendedGroup',
                    sectionname: BiContextProvidedValue,
                    widgetname: BiContextProvidedValue,
                    typeid: BiContextProvidedValue,
                    itemid: BiContextProvidedValue,
                  })}
                >
                  {t('networkBar.groups.group.actions.visit')}
                </GhostButton>
              }
              fillContainerWidth
              showSuffixOnlyOnHover
            />
          ) : null
        )}
      </StreamedList>
    </div>
  );
};
RecommendedGroupList.displayName = 'RecommendedGroupList';

export default RecommendedGroupList;
